"use client";
import { useEffect, useState, useCallback, useMemo } from 'react';
import { Markdown } from '../../components/Markdown';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { ProgressRing } from '../../components/ProgressRing';
import { ChevronDown, ChevronUp, Calendar, Flame } from 'lucide-react';
import { cn } from '../../lib/utils';

type RitualStage = 'prime' | 'learn' | 'voice' | 'do' | 'note';

type StageData = {
    id: RitualStage;
    label: string;
    completed: boolean;
    expanded: boolean;
};

const STAGES: Array<{ id: RitualStage; label: string }> = [
    { id: 'prime', label: 'Prime' },
    { id: 'learn', label: 'Learn' },
    { id: 'voice', label: 'Voice' },
    { id: 'do', label: 'Do' },
    { id: 'note', label: 'Note' },
];

export default function DailyPage() {
    const [md, setMd] = useState('');
    const [stages, setStages] = useState<StageData[]>(() =>
        STAGES.map(s => ({ id: s.id, label: s.label, completed: false, expanded: false }))
    );
    const [currentDate, setCurrentDate] = useState('');
    const [streak, setStreak] = useState(0);

    useEffect(() => {
        // Load markdown from the data directory
        fetch('/data/schedules/daily_routine.md')
            .then(r => (r.ok ? r.text() : ''))
            .then(setMd)
            .catch(() => setMd(''));

        // Set current date
        const now = new Date();
        setCurrentDate(now.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }));

        // Load streak from localStorage (mock data for now)
        const savedStreak = typeof window !== 'undefined' ? localStorage.getItem('sm_daily_streak') : null;
        if (savedStreak) {
            try {
                setStreak(parseInt(savedStreak, 10) || 0);
            } catch {}
        }
    }, []);

    const toggleStage = useCallback((stageId: RitualStage) => {
        setStages(prev =>
            prev.map(s => (s.id === stageId ? { ...s, expanded: !s.expanded } : s))
        );
    }, []);

    const completeStage = useCallback((stageId: RitualStage) => {
        setStages(prev =>
            prev.map(s => (s.id === stageId ? { ...s, completed: true } : s))
        );
    }, []);

    const progress = useMemo(() => {
        const completed = stages.filter(s => s.completed).length;
        return (completed / stages.length) * 100;
    }, [stages]);

    const handleExportReflection = useCallback(() => {
        // TODO: Implement export functionality
        console.log('Export reflection');
    }, []);

    return (
        <div className="flex flex-col min-h-full w-full">
            {/* Header */}
            <header className="mb-8 w-full">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 w-full">
                    <div className="min-w-0 flex-1">
                        <h1 className="text-[32px] leading-[40px] font-bold mb-2">Daily Ritual</h1>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground flex-wrap">
                            <div className="flex items-center gap-2">
                                <Calendar className="h-4 w-4 shrink-0" />
                                <span className="whitespace-nowrap">{currentDate}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <Flame className={cn("h-4 w-4 shrink-0 virtue-gold", streak > 0 && "animate-gentle-pulse")} />
                                <span className="font-medium whitespace-nowrap">{streak} day streak</span>
                            </div>
                        </div>
                    </div>
                    <div className="flex gap-2 shrink-0">
                        <Button variant="outline" size="default">History</Button>
                        <Button variant="outline" size="default">Settings</Button>
                    </div>
                </div>
            </header>

            {/* Content: Collapsible Sections */}
            <div className="flex-1 space-y-4 mb-8">
                {stages.map((stage, index) => (
                    <Card
                        key={stage.id}
                        className={cn(
                            'overflow-hidden transition-all duration-300',
                            stage.completed && 'opacity-75',
                            stage.expanded && 'shadow-lg'
                        )}
                    >
                        <button
                            onClick={() => toggleStage(stage.id)}
                            className="w-full p-6 flex items-center justify-between hover:bg-muted/50 transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded-t-2xl gap-4"
                            aria-expanded={stage.expanded}
                            aria-label={`Toggle ${stage.label} section`}
                        >
                            <div className="flex items-center gap-4 min-w-0 flex-1">
                                <div
                                    className={cn(
                                        'flex items-center justify-center w-8 h-8 rounded-full text-sm font-semibold shrink-0 transition-all duration-300',
                                        stage.completed
                                            ? 'bg-[hsl(var(--virtue-gold))] text-white shadow-sm'
                                            : 'bg-muted text-muted-foreground'
                                    )}
                                >
                                    {stage.completed ? (
                                        <span className="text-base leading-none">✓</span>
                                    ) : (
                                        index + 1
                                    )}
                                </div>
                                <h2 className="text-[24px] leading-[32px] font-semibold text-left truncate">{stage.label}</h2>
                            </div>
                            {stage.expanded ? (
                                <ChevronUp className="h-5 w-5 text-muted-foreground shrink-0" />
                            ) : (
                                <ChevronDown className="h-5 w-5 text-muted-foreground shrink-0" />
                            )}
                        </button>
                        <div
                            className={cn(
                                'overflow-hidden transition-all duration-300 ease-in-out',
                                stage.expanded ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
                            )}
                        >
                            <div className="px-6 pb-6 border-t border-border w-full">
                                {md && stage.id === 'prime' && (
                                    <div className="mt-4">
                                        <Markdown content={md} />
                                    </div>
                                )}
                                {stage.id !== 'prime' && (
                                    <div className="mt-4 text-foreground">
                                        <p className="leading-relaxed mb-4">
                                            {stage.id === 'learn' && 'Engage with today\'s micro-lesson or drill to deepen your understanding.'}
                                            {stage.id === 'voice' && 'Practice your eloquence through voice recording exercises.'}
                                            {stage.id === 'do' && 'Take action on what you\'ve learned through practical application.'}
                                            {stage.id === 'note' && 'Reflect and document your insights from today\'s practice.'}
                                        </p>
                                    </div>
                                )}
                                <Button
                                    onClick={() => completeStage(stage.id)}
                                    disabled={stage.completed}
                                    size="default"
                                    className="mt-4"
                                >
                                    {stage.completed ? 'Completed' : 'Mark as Complete'}
                                </Button>
                            </div>
                        </div>
                    </Card>
                ))}
            </div>

            {/* Footer: Progress Meter + Export Button */}
            <footer className="border-t border-border pt-6 mt-auto w-full">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-6 w-full">
                    <div className="flex items-center gap-4 min-w-0">
                        <ProgressRing progress={progress} size={64} showLabel className="shrink-0" completed={progress === 100} />
                        <div className="min-w-0">
                            <p className="text-sm font-medium">Progress</p>
                            <p className="text-xs text-muted-foreground">
                                {stages.filter(s => s.completed).length} of {stages.length} stages complete
                            </p>
                        </div>
                    </div>
                    <Button
                        onClick={handleExportReflection}
                        size="lg"
                        className="min-w-[200px] shrink-0"
                        disabled={progress < 100}
                    >
                        Export Reflection
                    </Button>
                </div>
            </footer>
        </div>
    );
}
